import type { Monaco } from "@monaco-editor/react";
import type { editor } from "monaco-editor";

import { processLiquidTemplate } from "../services/integrations/process-liquid-template";
import {
  entityPlaceholderRegex,
  liquidTemplatePlaceholderRegex,
  runtimePlaceholderRegex,
} from "./regex-definitions";

export interface ValidationResult {
  hasErrors: boolean;
  markers: editor.IMarker[];
}

export const createEntityTemplateValidator = (
  editor: editor.IStandaloneCodeEditor,
  monaco: Monaco,
): (() => ValidationResult) => {
  const model = editor.getModel();
  if (!model) {
    throw new Error("Editor model not found");
  }

  return () => {
    const text = model.getValue();
    const diagnostics: editor.IMarker[] = [];
    let match;

    // Create a new RegExp instance to avoid mutating the shared regex's lastIndex
    const entityRegexInstance = new RegExp(
      entityPlaceholderRegex.source,
      entityPlaceholderRegex.flags,
    );

    while ((match = entityRegexInstance.exec(text)) !== null) {
      const content = match[1].trim();
      const isRuntimePlaceholder = runtimePlaceholderRegex.test(content);

      if (
        !window._latestEntityList.includes(content) &&
        !isRuntimePlaceholder
      ) {
        diagnostics.push({
          endColumn: model.getPositionAt(match.index + match[0].length).column,
          endLineNumber: model.getPositionAt(match.index + match[0].length)
            .lineNumber,
          message: `"${content}" is not a valid entry in the entity list.`,
          owner: "liquid",
          resource: model.uri,
          severity: monaco.MarkerSeverity.Error,
          startColumn: model.getPositionAt(match.index).column,
          startLineNumber: model.getPositionAt(match.index).lineNumber,
        });
      }
    }

    // Set the markers on the model with a unique owner to avoid conflicts
    monaco.editor.setModelMarkers(model, "entity", diagnostics);

    return {
      hasErrors: diagnostics.length > 0,
      markers: diagnostics,
    };
  };
};

export const createLiquidTemplateValidator = (
  editor: editor.IStandaloneCodeEditor,
  monaco: Monaco,
): (() => Promise<ValidationResult>) => {
  const model = editor.getModel();
  if (!model) {
    throw new Error("Editor model not found");
  }

  return async () => {
    const text = model.getValue();
    const diagnostics: editor.IMarker[] = [];
    let match;

    // Create a new RegExp instance to avoid mutating the shared regex's lastIndex
    const liquidRegexInstance = new RegExp(
      liquidTemplatePlaceholderRegex.source,
      liquidTemplatePlaceholderRegex.flags,
    );

    while ((match = liquidRegexInstance.exec(text)) !== null) {
      const content = match[1].trim();
      const fullMatch = match[0]; // The full {{ ... }} match

      try {
        const result = await new Promise<string>((resolve, reject) => {
          const processFn = processLiquidTemplate(
            fullMatch,
            (result) => resolve(result),
            (error) => reject(new Error(error)),
          );
          processFn();
        });

        // Check if result is the same as content (template didn't process)
        if (
          result === content ||
          result.trim() === content.trim() ||
          result.trim() === ""
        ) {
          diagnostics.push({
            endColumn: model.getPositionAt(match.index + match[0].length)
              .column,
            endLineNumber: model.getPositionAt(match.index + match[0].length)
              .lineNumber,
            message: `Liquid template did not evaluate. Result is the same as input.`,
            owner: "liquid",
            resource: model.uri,
            severity: monaco.MarkerSeverity.Error,
            startColumn: model.getPositionAt(match.index).column,
            startLineNumber: model.getPositionAt(match.index).lineNumber,
          });
        }
      } catch (error) {
        // Template evaluation failed
        const errorMessage =
          error instanceof Error ? error.message : "Unknown error occurred";
        diagnostics.push({
          endColumn: model.getPositionAt(match.index + match[0].length).column,
          endLineNumber: model.getPositionAt(match.index + match[0].length)
            .lineNumber,
          message: `Liquid template evaluation failed: ${errorMessage}`,
          owner: "liquid",
          resource: model.uri,
          severity: monaco.MarkerSeverity.Error,
          startColumn: model.getPositionAt(match.index).column,
          startLineNumber: model.getPositionAt(match.index).lineNumber,
        });
      }
    }

    // Set the markers on the model
    monaco.editor.setModelMarkers(model, "liquid", diagnostics);

    return {
      hasErrors: diagnostics.length > 0,
      markers: diagnostics,
    };
  };
};
